import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import apiService from '../services/api';

// Helper function to parse AI summary into sections
const parseAiSummary = (summary) => {
  if (!summary) return { insights: '', trends: '', risks: '', actions: '' };
  
  // Split by the section headers
  const insightsMatch = summary.match(/###\s*Key Insights[\s\S]*?(?=###|$)/i);
  const trendsMatch = summary.match(/###\s*Positive Trends[\s\S]*?(?=###|$)/i);
  const risksMatch = summary.match(/###\s*Risks[^\n]*[\s\S]*?(?=###|$)/i);
  const actionsMatch = summary.match(/###\s*Recommended Actions[\s\S]*?(?=###|$)/i);
  
  return {
    insights: insightsMatch ? insightsMatch[0].replace(/###\s*Key Insights\s*/i, '').trim() : '',
    trends: trendsMatch ? trendsMatch[0].replace(/###\s*Positive Trends\s*/i, '').trim() : '',
    risks: risksMatch ? risksMatch[0].replace(/###\s*Risks[^\n]*\s*/i, '').trim() : '',
    actions: actionsMatch ? actionsMatch[0].replace(/###\s*Recommended Actions\s*/i, '').trim() : ''
  };
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    departments: 0,
    areas: 0,
    people: 0,
    metrics: 0,
    metricValues: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState(null);
  const [aiSummaryExpanded, setAiSummaryExpanded] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [departments, areas, people, metrics, metricValues] = await Promise.all([
          apiService.departments.getAll(),
          apiService.areas.getAll(),
          apiService.people.getAll(),
          apiService.metrics.getAll(),
          apiService.metricValues.getAll()
        ]);

        setStats({
          departments: departments.data.length,
          areas: areas.data.length,
          people: people.data.length,
          metrics: metrics.data.length,
          metricValues: metricValues.data.length
        });
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    fetchAiSummary();
  }, []);

  const fetchAiSummary = async (forceRefresh = false) => {
    try {
      setAiSummaryLoading(true);
      setAiSummaryError(null);
      const response = await apiService.aiSummary.get(forceRefresh);
      setAiSummary(response.data);
    } catch (err) {
      // Extract error message from response
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load AI summary';
      setAiSummaryError(errorMessage);
      console.error('AI Summary error:', err);
    } finally {
      setAiSummaryLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
      </div>

      {/* AI Summary Section */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>AI-Powered Metrics Summary</h3>
          <button 
            className="btn btn-secondary btn-small"
            onClick={() => fetchAiSummary(true)}
            disabled={aiSummaryLoading}
            style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
          >
            {aiSummaryLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div style={{ padding: '1.5rem' }}>
          {aiSummaryLoading && !aiSummary && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
              Generating AI summary...
            </div>
          )}
          {aiSummaryError && !aiSummary && (
            <div className="error" style={{ 
              margin: 0, 
              padding: '1rem',
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              color: '#721c24'
            }}>
              <strong>Error loading AI summary:</strong><br />
              {aiSummaryError}
            </div>
          )}
          {aiSummary && (() => {
            const sections = parseAiSummary(aiSummary.summary);
            return (
              <div>
                {aiSummary.warning && (
                  <div className="warning-banner" style={{ marginBottom: '1rem' }}>
                    {aiSummary.warning}
                  </div>
                )}
                {aiSummaryExpanded && (
                  <div className="ai-summary-four-column" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '1.5rem',
                    marginBottom: '1rem'
                  }}>
                  {/* Key Insights Column */}
                  <div style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '1rem',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <h4 style={{
                      margin: '0 0 0.75rem 0',
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#032E36',
                      borderBottom: '2px solid #034955',
                      paddingBottom: '0.5rem'
                    }}>
                      Key Insights
                    </h4>
                    <div className="ai-summary-content" style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#032E36' }}>
                      <ReactMarkdown>{sections.insights}</ReactMarkdown>
                    </div>
                  </div>
                  
                  {/* Positive Trends Column */}
                  <div style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '1rem',
                    backgroundColor: '#f0fff4'
                  }}>
                    <h4 style={{
                      margin: '0 0 0.75rem 0',
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#032E36',
                      borderBottom: '2px solid #28a745',
                      paddingBottom: '0.5rem'
                    }}>
                      Positive Trends
                    </h4>
                    <div className="ai-summary-content" style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#032E36' }}>
                      <ReactMarkdown>{sections.trends}</ReactMarkdown>
                    </div>
                  </div>
                  
                  {/* Risks & Deviations Column */}
                  <div style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '1rem',
                    backgroundColor: '#fff'
                  }}>
                    <h4 style={{
                      margin: '0 0 0.75rem 0',
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#032E36',
                      borderBottom: '2px solid #dc3545',
                      paddingBottom: '0.5rem'
                    }}>
                      Risks & Deviations
                    </h4>
                    <div className="ai-summary-content" style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#032E36' }}>
                      <ReactMarkdown>{sections.risks}</ReactMarkdown>
                    </div>
                  </div>
                  
                  {/* Recommended Actions Column */}
                  <div style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '1rem',
                    backgroundColor: '#f0f8ff'
                  }}>
                    <h4 style={{
                      margin: '0 0 0.75rem 0',
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#032E36',
                      borderBottom: '2px solid #007bff',
                      paddingBottom: '0.5rem'
                    }}>
                      Recommended Actions
                    </h4>
                    <div className="ai-summary-content" style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#032E36' }}>
                      <ReactMarkdown>{sections.actions}</ReactMarkdown>
                    </div>
                  </div>
                  </div>
                )}
                
                {/* Footer with generation info and toggle */}
                <div style={{ 
                  marginTop: '1rem', 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.875rem', 
                  color: '#6c757d',
                  borderTop: '1px solid #e0e0e0',
                  paddingTop: '1rem'
                }}>
                  <span>
                    Generated: {new Date(aiSummary.generated_at).toLocaleString()}
                    {aiSummary.cached && ' (Cached)'}
                  </span>
                  <button
                    className="btn btn-link"
                    onClick={() => setAiSummaryExpanded(!aiSummaryExpanded)}
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.25rem 0.5rem',
                      textDecoration: 'none',
                      color: '#034955',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none'
                    }}
                  >
                    {aiSummaryExpanded ? 'Show Less' : 'Show More'}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-number">{stats.departments}</div>
          <div className="stat-label">Departments</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.areas}</div>
          <div className="stat-label">Areas</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.people}</div>
          <div className="stat-label">People</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.metrics}</div>
          <div className="stat-label">Metrics</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.metricValues}</div>
          <div className="stat-label">Metric Values</div>
        </div>
      </div>

      <div className="grid">
        <div className="card">
          <div className="card-header">
            <h3>Welcome to Metrics Management System</h3>
          </div>
          <p>Use the navigation above to manage your organization's metrics data:</p>
          <ul style={{marginTop: '1rem', paddingLeft: '1.5rem'}}>
            <li><strong>Departments</strong> - Organize by business units</li>
            <li><strong>Areas</strong> - Define specific operational areas</li>
            <li><strong>People</strong> - Manage team members and responsibilities</li>
            <li><strong>Metrics</strong> - Define key performance indicators</li>
            <li><strong>Metric Values</strong> - Track actual measurements and targets</li>
          </ul>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Quick Actions</h3>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            <a href="/metrics" className="btn btn-primary">Create Metric</a>
            <a href="/metric-values" className="btn btn-accent">Record Metric Value</a>
            <a href="/departments" className="btn btn-secondary">Manage Departments</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;